import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";

import {
  JP_SIGNING_CONSTANTS,
  SIGNING_PROVIDER,
} from "../../../constants/SigningProviderConstants";
import {
  FILE_SIZE_VALIDATION_10MB,
  MAX_10MB_FILE_SIZE,
} from "../../../constants/ManualRequestFormConstants";
import { manualRequestFormActions } from "../../../store/actions/vorgange/manualRequestFormActions";
import { signingProviderActions } from "../../../store/actions/vorgange/signingProviderActions";
import carLogo from "../../../assets/images/my_digital_car.svg";

import MDCButton from "../../core/MDCButton";
import MDCDatePicker from "../../core/MDCDatePicker";
import MDCDocumentUpload from "../../shared/MDCDocumentUpload";
import MDCLabel from "../../core/MDCLabel";
import MDCModal from "../../core/MDCModal";
import MDCRegisterEmail from "../../core/MDCRegisterEmail";
import MDCSelect from "../../core/MDCSelect";
import MDCText from "../../core/MDCText";
import SignerSelectionPanel from "./SignerSelectionPanel";
import {
  dbFormatDate,
  getFileExtension,
  getFileMIMEType,
} from "../../../helpers/utils";
import ErrorText from "../../shared/ErrorText";

const useStyles = makeStyles((theme) => ({
  error: { color: theme.palette.MDCColors.errorDanger, marginTop: ".75rem" },
  modalHeader: {
    maxWidth: "520px",
    margin: "0 auto",
    padding: "24px 0 16px",
    alignItems: "center",
    textAlign: "left",
    font: "normal normal normal 20px/24px Fira Sans",
    letterSpacing: "0px",
    color: "#000000",
  },
  modalTitle: { color: theme.palette.MDCColors.black },
  popupHeader: {
    color: "#000000",
    textAlign: "left",
    font: "normal normal normal 20px/24px Fira Sans",
    letterSpacing: "0px",
    opacity: 1,
  },

  modalContentWrapper: {
    // maxWidth: "520px",
    // margin: "0 auto",
    padding: "0 30px",
  },
  modalFieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
    marginTop: "1rem",
    // "& .MuiFormControl-marginNormal": {
    //   marginTop: "0.625rem",
    //   marginBottom: 0,
    // },
  },
  t: { color: theme.palette.MDCColors.color15 },
  headerCss: {
    padding: "2rem",
    alignItems: "center",
  },
  headerColor: { color: theme.palette.MDCColors.black },
  fieldGroup: {
    marginBottom: ".5rem !important",
  },
}));

const emailPattern =
  /^(?=.{1,200}$)(?!.*\.\.)(?!.*@.*@)[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,10}$/;

// const namePattern =
//   /([ - ]| |[ -~]|[ -¬]|[®-ž]|[Ƈ-ƈ]|Ə|Ɨ|[Ơ-ơ]|[Ư-ư]|Ʒ|[Ǎ-ǜ]|[Ǟ-ǟ]|[Ǣ-ǰ]|[Ǵ-ǵ]|[Ǹ-ǿ]|[Ȓ-ȓ]|[Ș-ț]|[Ȟ-ȟ]|[ȧ-ȳ]|ə|ɨ|ʒ|[ʹ-ʺ]|[ʾ-ʿ]|ˈ|ˌ|[Ḃ-ḃ]|[Ḇ-ḇ]|[Ḋ-ḑ]|[Ḝ-ḫ]|[ḯ-ḷ]|[Ḻ-ḻ]|[Ṁ-ṉ]|[Ṓ-ṛ]|[Ṟ-ṣ]|[Ṫ-ṯ]|[Ẁ-ẇ]|[Ẍ-ẗ]|ẞ|[Ạ-ỹ]|’|‡|€|A̋|C(̀|̄|̆|̈|̕|̣|̦|̨̆)|D̂|F(̀|̄)|G̀|H(̄|̦|̱)|J(́|̌)|K(̀|̂|̄|̇|̕|̛|̦|͟H|͟h)|L(̂|̥|̥̄|̦)|M(̀|̂|̆|̐)|N(̂|̄|̆|̦)|P(̀|̄|̕|̣)|R(̆|̥|̥̄)|S(̀|̄|̛̄|̱)|T(̀|̄|̈|̕|̛)|U̇|Z(̀|̄|̆|̈|̧)|a̋|c(̀|̄|̆|̈|̕|̣|̦|̨̆)|d̂|f(̀|̄)|g̀|h(̄|̦)|j́|k(̀|̂|̄|̇|̕|̛|̦|͟h)|l(̂|̥|̥̄|̦)|m(̀|̂|̆|̐)|n(̂|̄|̆|̦)|p(̀|̄|̕|̣)|r(̆|̥|̥̄)|s(̀|̄|̛̄|̱)|t(̀|̄|̕|̛)|u̇|z(̀|̄|̆|̈|̧)|Ç̆|Û̄|ç̆|û̄|ÿ́|Č(̕|̣)|č(̕|̣)|Ī́|ī́|Ž(̦|̧)|ž(̦|̧)|Ḳ̄|ḳ̄|Ṣ̄|ṣ̄|Ṭ̄|ṭ̄|Ạ̈|ạ̈|Ọ̈|ọ̈|Ụ(̄|̈)|ụ(̄|̈))*/u;

/**
 * SignerSelectionScreen - Encapsulates all representative/signer selection logic
 * Manages proxy signers, validation, and submission for legal person QES signing
 *
 * @param {Object} props
 * @param {string} props.requestId - Request identifier
 * @param {Array} props.representatives - Initial representatives list from API
 * @param {Object} props.provider - Selected QES provider
 * @param {boolean} props.isPPOA - Is this a PPOA signing flow
 * @param {Function} props.onGetPPOASigningStatus - Callback for PPOA status polling
 * @param {Function} props.onComplete - Callback when signers are submitted successfully
 * @param {Function} props.onBack - Callback to go back to company selection
 * @param {Object} props.classes - External styles (optional)
 * @param {boolean} props.documentsAvailable - Are documents available
 * @param {boolean} props.addressDocumentAvailable - Is address document available
 * @returns {JSX.Element}
 */
const SignerSelectionScreen = ({
  isDetailDialog,
  requestId,
  representatives: initialRepresentatives = [],
  provider,
  isPPOA = false,
  onGetPPOASigningStatus = null,
  onComplete = null,
  onBack = null,
  classes: externalClasses = null,
  documentsAvailable = false,
  addressDocumentAvailable = false,
  companyName = "",
  hrab = "",
  disabled = false,
}) => {
  const localClasses = useStyles();
  const classes = externalClasses
    ? { ...localClasses, ...externalClasses }
    : localClasses;
  const dispatch = useDispatch();

  // Local state for signer selection
  const [representatives, setRepresentatives] = useState(
    initialRepresentatives
  );
  const [proxySigners, setProxySigners] = useState([]);
  const [proxyModalOpen, setProxyModalOpen] = useState(false);
  const [proxyDraft, setProxyDraft] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    email: "",
    poaDocument: null,
    onBehalfOfId: "",
    editId: null,
  });
  const [proxyValidationErrors, setProxyValidationErrors] = useState({});
  const [documentValidationAttempt, setDocumentValidationAttempt] = useState(0);
  const [proxySignerErrors, setProxySignerErrors] = useState({});
  const [flowError, setFlowError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Transform and sync representatives on initial mount only
  useEffect(() => {
    if (initialRepresentatives && initialRepresentatives.length > 0) {
      const transformedRepresentatives = initialRepresentatives.map((rep) => {
        // Handle different possible data structures from API
        let name = rep.name || rep.displayName || "";
        let firstName = rep.firstName || rep.givenName || "";
        let lastName = rep.lastName || rep.surname || rep.familyName || "";
        let birthDate = rep.birthDate || rep.dateOfBirth || rep.birthdate || "";
        let id = rep.id || rep.representativeId || "";

        // If name exists but firstName/lastName don't, try to parse
        if (name && !firstName && !lastName) {
          const nameParts = name.trim().split(/\s+/);
          firstName = nameParts[0] || "";
          lastName = nameParts.slice(1).join(" ") || "";
        }
        // If firstName/lastName exist but name doesn't, combine them
        else if (!name && (firstName || lastName)) {
          name = `${firstName} ${lastName}`.trim();
        }

        return {
          id: id,
          name: name,
          firstName: firstName,
          lastName: lastName,
          birthDate: birthDate,
          email: rep.email || rep.emailAddress || "",
          role: rep.role || rep.position || rep.jobTitle || rep.state || "",
          selected: rep.selected ?? false,
          disabled: rep.disabled ?? false,
        };
      });
      setRepresentatives(transformedRepresentatives);
    } else {
      setRepresentatives([]);
    }
  }, []);

  const updateRepresentative = (id, changes) =>
    setRepresentatives((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item))
    );

  /**
   * Validate proxy signer form fields in real-time
   */
  const validateProxyForm = (form) => {
    const errors = {};

    if (!form.onBehalfOfId) {
      errors.onBehalfOfId =
        "Dies ist ein Pflichtfeld. Bitte treffen Sie eine Auswahl.";
    }
    if (!form.firstName || form.firstName.trim() === "") {
      errors.firstName = "Bitte geben Sie einen gültigen Wert ein.";
    }
    // else if (!namePattern.test(form.firstName)) {
    //   errors.firstName = "Bitte geben Sie einen gültigen Wert ein.";
    // }
    if (!form.lastName || form.lastName.trim() === "") {
      errors.lastName = "Bitte geben Sie einen gültigen Wert ein.";
    }
    // else if (!namePattern.test(form.lastName)) {
    //   errors.lastName = "Bitte geben Sie einen gültigen Wert ein.";
    // }
    if (!form.email || form.email.trim() === "") {
      errors.email = "Bitte geben Sie einen gültigen Wert ein.";
    } else if (!emailPattern.test(form.email)) {
      errors.email = "Bitte geben Sie einen gültigen Wert ein.";
    }
    if (!form.birthDate) {
      errors.birthDate = "Bitte geben Sie einen gültigen Wert ein.";
    }
    if (!form.poaDocument) {
      errors.poaDocument =
        "Bitte laden Sie alle aufgeführten Dokumente hoch, bevor Sie fortfahren.";
    }

    return errors;
  };

  const confirmProxySigner = () => {
    const errors = validateProxyForm(proxyDraft);

    if (!proxyDraft.poaDocument) {
      // Reset the upload's internal file error to the required-document error.
      setDocumentValidationAttempt((attempt) => attempt + 1);
    }

    if (Object.keys(errors).length > 0) {
      setProxyValidationErrors(errors);
      setFlowError("Bitte füllen Sie alle erforderlichen Felder korrekt aus.");
      return;
    }

    setProxyValidationErrors({});
    setFlowError("");

    const onBehalfOf = representativeList?.find(
      (representative) => representative.id === proxyDraft.onBehalfOfId
    );
    if (!onBehalfOf) {
      // setFlowError("Die ausgewählte Person konnte nicht gefunden werden.");
      setProxyValidationErrors({
        onBehalfOfId: "Die ausgewählte Person konnte nicht gefunden werden.",
      });
      return;
    }

    const previousProxySigner = proxySigners.find(
      (signer) => signer.id === proxyDraft.editId
    );
    const previousOnBehalfOfId = previousProxySigner?.onBehalfOfId;

    setRepresentatives((current) =>
      current.map((representative) => ({
        ...representative,
        selected:
          representative.id === onBehalfOf.id
            ? true
            : representative.id === previousOnBehalfOfId
            ? false
            : representative.selected,
        disabled:
          representative.id === onBehalfOf.id
            ? true
            : representative.id === previousOnBehalfOfId
            ? false
            : representative.disabled === true,
      }))
    );

    const updatedProxySigner = {
      id: proxyDraft.editId || `proxy-${Date.now()}`,
      ...proxyDraft,
      isNonRepresentative: true,
      onBehalfOfName: onBehalfOf.name,
    };

    setProxySigners((current) =>
      proxyDraft.editId
        ? current.map((signer) =>
            signer.id === proxyDraft.editId ? updatedProxySigner : signer
          )
        : [...current, updatedProxySigner]
    );
    setProxySignerErrors((current) => {
      const updatedErrors = { ...current };
      delete updatedErrors[updatedProxySigner.id];
      return updatedErrors;
    });

    setProxyModalOpen(false);
    setFlowError("");
  };

  const getLegalRepresentative = (representative) => {
    const nameParts = representative.name.trim().split(/\s+/);
    return {
      id: representative.id,
      firstName: representative.firstName || nameParts[0] || "",
      lastName: representative.lastName || nameParts.slice(1).join(" ") || "",
      role: representative.role || "Geschäftsführer",
    };
  };

  const createLegalPersonCase = async (setRepresentativeEmailErrors) => {
    if (!requestId) {
      setFlowError("Anfrage-ID fehlt. Bitte laden Sie die Seite neu.");
      return;
    }

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    const selectedRepresentatives =
      representativeList?.filter((item) => item.selected && !item.disabled) ||
      [];

    const signers = [...selectedRepresentatives, ...proxySigners];

    // if (!signers.length) {
    //   setFlowError(JP_SIGNING_CONSTANTS.MISSING_SIGNER);
    //   return;
    // }

    // if (signers.some((signer) => !emailPattern.test(signer.email || ""))) {
    //   setFlowError(JP_SIGNING_CONSTANTS.MISSING_SIGNER_EMAIL);
    //   return;
    // }

    setIsSubmitting(true);
    setFlowError("");
    setProxySignerErrors({});

    const representativeSigners = selectedRepresentatives.map(
      (representative) => ({
        type: "REPRESENTATIVE",
        representativeId: representative.id,
        givenName: getLegalRepresentative(representative).firstName,
        surname: getLegalRepresentative(representative).lastName,
        email: representative.email,
        birthDate: representative.birthDate,
      })
    );

    const nonRepresentativeSigners = proxySigners.map((proxySigner) => {
      return {
        type: "NON_REPRESENTATIVE",
        signingOnBehalfOfRepresentativeId: proxySigner.onBehalfOfId,
        givenName: proxySigner.firstName,
        surname: proxySigner.lastName,
        email: proxySigner.email,
        birthDate: dbFormatDate(proxySigner?.birthDate),
        power_of_attorney_document: {
          reference:
            proxySigner.poaDocument?.name &&
            `POA-Dokument.${getFileExtension(proxySigner.poaDocument?.name)}`,
          name: getFileMIMEType(proxySigner.poaDocument?.name),
          content: proxySigner.poaDocument?.value || "",
        },
      };
    });

    const signerPayload = [
      ...representativeSigners,
      ...nonRepresentativeSigners,
    ];

    try {
      // Step 1: Submit signers and wait for response
      const submitResponse = await dispatch(
        signingProviderActions.submitSigners(requestId, signerPayload, isPPOA)
      );

      if (submitResponse?.status < 200 || submitResponse?.status >= 300) {
        setFlowError("Die Unterzeichner konnten nicht gespeichert werden.");
        setIsSubmitting(false);
        return;
      }

      // Step 2: Create signing case and wait for API response before proceeding
      const resolvedProviderId =
        provider?.id ||
        provider?.key ||
        provider?.providerId ||
        SIGNING_PROVIDER.YOUTRUST;

      const signingCaseResponse = await dispatch(
        signingProviderActions.createSigningCase(requestId, {
          providerId: resolvedProviderId,
          providerName: provider?.name || "YouTrust",
          companyName: companyName,
          hrab,
          signers,
          documentsAvailable,
          addressDocumentAvailable,
        })
      );

      // Verify signing case was created successfully
      if (!signingCaseResponse || signingCaseResponse?.status >= 400) {
        setFlowError("Der Signaturfall konnte nicht erstellt werden.");
        setIsSubmitting(false);
        return;
      }

      // Step 3: Fetch status for immediate UI update
      if (isPPOA && onGetPPOASigningStatus) {
        onGetPPOASigningStatus();
      } else {
        dispatch(manualRequestFormActions.getVollmachtSigningStatus(requestId));
      }

      // Step 4: Only proceed to next screen after all APIs complete successfully
      setIsSubmitting(false);
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Error in createLegalPersonCase:", error);
      const errorFieldMap =
        error?.response?.data?.errorFieldMap || error?.data?.errorFieldMap;
      const representativeEmailErrors = {};
      const proxyErrors = {};
      const fieldNameByApiField = {
        email: "email",
        givenName: "firstName",
        surname: "lastName",
        birthDate: "birthDate",
        signingOnBehalfOfRepresentativeId: "onBehalfOfId",
      };

      Object.entries(errorFieldMap || {}).forEach(([fieldPath, message]) => {
        const match = fieldPath.match(/^signers\[(\d+)\]\.(.+)$/);
        if (!match) return;

        const signerIndex = Number(match[1]);
        const apiField = match[2];
        const representative = selectedRepresentatives[signerIndex];
        if (representative) {
          if (apiField === "email") {
            representativeEmailErrors[representative.id] = message;
          }
          return;
        }

        const proxySigner =
          proxySigners[signerIndex - selectedRepresentatives.length];
        const fieldName =
          fieldNameByApiField[apiField] ||
          (apiField.startsWith("power_of_attorney_document")
            ? "poaDocument"
            : null);
        if (proxySigner && fieldName) {
          proxyErrors[proxySigner.id] = {
            ...proxyErrors[proxySigner.id],
            [fieldName]: message,
          };
        }
      });

      setRepresentativeEmailErrors?.(representativeEmailErrors);
      setProxySignerErrors(proxyErrors);
      if (
        Object.keys(representativeEmailErrors).length ||
        Object.keys(proxyErrors).length
      ) {
        setFlowError("");
      } else {
        setFlowError(
          errorFieldMap?.message ||
            "Ein Fehler ist beim Signaturprozess aufgetreten. Bitte versuchen Sie es erneut."
        );
      }
      setIsSubmitting(false);
    }
  };

  const downloadProxyDocument = (proxySigner) => {
    if (!proxySigner?.poaDocument) return;

    const blob =
      typeof proxySigner.poaDocument?.value === "string"
        ? new Blob(
            [
              Uint8Array.from(atob(proxySigner.poaDocument?.value), (char) =>
                char.charCodeAt(0)
              ),
            ],
            { type: getFileMIMEType(proxySigner.poaDocument?.name) }
          )
        : proxySigner?.poaDocument?.value;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `POA-Dokument.${getFileExtension(
      proxySigner.poaDocument?.name
    )}`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const representativeList = representatives?.length > 0 ? representatives : [];

  return (
    <>
      <SignerSelectionPanel
        isDetailDialog={isDetailDialog}
        classes={classes}
        representatives={representativeList}
        proxySigners={proxySigners}
        flowError={flowError}
        selectRepresentative={(id, selected) =>
          updateRepresentative(id, { selected })
        }
        updateRepresentative={updateRepresentative}
        openProxySignerModal={() => {
          setProxyDraft({
            firstName: "",
            lastName: "",
            birthDate: "",
            email: "",
            poaDocument: null,
            onBehalfOfId: "",
            editId: null,
          });
          setProxyValidationErrors({});
          setFlowError("");
          setProxyModalOpen(true);
        }}
        editProxySigner={(proxySigner) => {
          setProxyDraft({ ...proxySigner, editId: proxySigner.id });
          setProxyValidationErrors(proxySignerErrors[proxySigner.id] || {});
          setFlowError("");
          setProxyModalOpen(true);
        }}
        removeProxySigner={(proxySigner) => {
          setRepresentatives((current) =>
            current.map((representative) =>
              representative.id === proxySigner.onBehalfOfId
                ? { ...representative, selected: false, disabled: false }
                : representative
            )
          );
          setProxySigners((current) =>
            current.filter((signer) => signer.id !== proxySigner.id)
          );
        }}
        downloadProxyDocument={downloadProxyDocument}
        goBack={onBack}
        continueToSigning={createLegalPersonCase}
        selectSignersHelp={JP_SIGNING_CONSTANTS.SELECT_SIGNERS_HELP1}
        selectSignersHeading={JP_SIGNING_CONSTANTS.SELECT_SIGNERS_HELP2}
        disabled={disabled || isSubmitting}
      />

      <MDCModal
        isScroll
        open={proxyModalOpen}
        title={
          <Grid container className={classes.headerCss}>
            <Grid item xs={8} className={classes.headerColor}>
              <h4> {JP_SIGNING_CONSTANTS.ADD_NON_REPRESENTATIVE}</h4>
            </Grid>
            <Grid item xs={4}>
              <img alt="My Digital Car" src={carLogo} width="95%" />
            </Grid>
          </Grid>
        }
        body={
          <Grid container flexDirection="column" padding="2rem">
            <div className={classes.fieldGroup}>
              <MDCLabel label="Bitte Unterzeichner auswählen" />
              <MDCSelect
                name="proxy-on-behalf-of"
                value={proxyDraft.onBehalfOfId}
                options={representativeList
                  .filter(
                    (representative) =>
                      !representative?.selected ||
                      representative.id === proxyDraft.onBehalfOfId
                  )
                  .map((representative) => ({
                    label: representative.name && `${representative.name}`,
                    value: representative.id,
                  }))}
                placeholder="Vertretungsberechtigte Person auswählen"
                handleChange={(event) => {
                  setProxyDraft({
                    ...proxyDraft,
                    onBehalfOfId: event.target.value,
                  });
                  if (event.target.value) {
                    setProxyValidationErrors((current) => ({
                      ...current,
                      onBehalfOfId: "",
                    }));
                  }
                }}
                error={!!proxyValidationErrors.onBehalfOfId}
              />
              {proxyValidationErrors.onBehalfOfId && (
                <ErrorText error={proxyValidationErrors.onBehalfOfId} />
              )}
              <div className={classes.fieldGroup}>
                <MDCDocumentUpload
                  key={documentValidationAttempt}
                  documentName="Vollmacht hochladen"
                  uploadedFileData={proxyDraft.poaDocument?.value}
                  error={proxyValidationErrors.poaDocument}
                  handleChange={(event) => {
                    const document = event?.target || null;

                    setProxyDraft({
                      ...proxyDraft,
                      poaDocument: document,
                    });
                    if (document?.value) {
                      setProxyValidationErrors((current) => ({
                        ...current,
                        poaDocument: "",
                      }));
                    } else {
                      // Remount the upload so its internal file state is reset
                      setDocumentValidationAttempt((attempt) => attempt + 1);
                    }
                  }}
                  acceptFileFormats=".pdf,.png,.jpg,.jpeg"
                  allowedExtensions={["pdf", "png", "jpg", "jpeg"]}
                  maxFileSize={MAX_10MB_FILE_SIZE}
                  isDetailDialog
                  fileSizeError={FILE_SIZE_VALIDATION_10MB}
                />
                {!proxyDraft.poaDocument && (
                  <div className={classes.t}>
                    {JP_SIGNING_CONSTANTS.POA_HINT}
                  </div>
                )}
              </div>
              <div className={classes.modalFieldRow}>
                <div>
                  <MDCLabel label={JP_SIGNING_CONSTANTS.FIRST_NAME} />
                  <MDCText
                    name="proxy-first-name"
                    value={proxyDraft.firstName}
                    onChange={(event) => {
                      const value = event.target.value;
                      setProxyDraft({
                        ...proxyDraft,
                        firstName: value,
                      });
                      if (value.trim() !== "") {
                        setProxyValidationErrors((current) => ({
                          ...current,
                          firstName: "",
                        }));
                      }
                    }}
                    maxLength={60}
                    error={!!proxyValidationErrors.firstName}
                  />
                  {proxyValidationErrors.firstName && (
                    <ErrorText error={proxyValidationErrors.firstName} />
                  )}
                </div>
                <div>
                  <MDCLabel label={JP_SIGNING_CONSTANTS.LAST_NAME} />
                  <MDCText
                    name="proxy-last-name"
                    value={proxyDraft.lastName}
                    onChange={(event) => {
                      const value = event.target.value;
                      setProxyDraft({
                        ...proxyDraft,
                        lastName: value,
                      });
                      if (value.trim() !== "") {
                        setProxyValidationErrors((current) => ({
                          ...current,
                          lastName: "",
                        }));
                      }
                    }}
                    maxLength={45}
                    error={!!proxyValidationErrors.lastName}
                  />
                  {proxyValidationErrors.lastName && (
                    <ErrorText error={proxyValidationErrors.lastName} />
                  )}
                </div>
              </div>
              <div className={classes.modalFieldRow}>
                <div>
                  <MDCLabel label={JP_SIGNING_CONSTANTS.REPRESENTATIVE_EMAIL} />
                  <MDCRegisterEmail
                    name="proxy-email"
                    type="email"
                    disableUnderline
                    value={proxyDraft.email}
                    allowCopyPaste
                    onChange={(event) => {
                      const value = event.target.value;
                      setProxyDraft({ ...proxyDraft, email: value });
                      if (emailPattern.test(value)) {
                        setProxyValidationErrors((current) => ({
                          ...current,
                          email: "",
                        }));
                      }
                    }}
                    error={!!proxyValidationErrors.email}
                  />
                  {proxyValidationErrors.email && (
                    <ErrorText error={proxyValidationErrors.email} />
                  )}
                </div>
                <div>
                  <MDCLabel label={JP_SIGNING_CONSTANTS.BIRTH_DATE} />
                  <MDCDatePicker
                    isManualEntry
                    value={proxyDraft.birthDate}
                    maxDate={new Date()}
                    onChange={(date) => {
                      setProxyDraft({ ...proxyDraft, birthDate: date });
                      if (date) {
                        setProxyValidationErrors((current) => ({
                          ...current,
                          birthDate: "",
                        }));
                      }
                    }}
                    error={!!proxyValidationErrors.birthDate}
                  />
                  {proxyValidationErrors.birthDate && (
                    <ErrorText error={proxyValidationErrors.birthDate} />
                  )}
                </div>
              </div>
            </div>
          </Grid>
        }
        footer={
          <Grid
            container
            pb={3}
            paddingX={"2rem"}
            justifyContent="space-between"
          >
            <Grid item>
              <MDCButton
                variant="outlined"
                label={JP_SIGNING_CONSTANTS.CANCEL}
                onClick={() => {
                  setProxyModalOpen(false);
                  setProxyDraft((current) => ({
                    ...current,
                    editId: null,
                  }));
                }}
                disabled={disabled}
              />
            </Grid>

            <Grid item>
              <MDCButton
                variant="contained"
                label={JP_SIGNING_CONSTANTS.CONFIRM}
                onClick={confirmProxySigner}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        }
        size="md"
      />
    </>
  );
};

export default SignerSelectionScreen;
